import React, { useState, createContext, useContext } from "react";
import { useApiIsLoaded } from "@vis.gl/react-google-maps";
import { useStoreSearch, GooglePlaceResult } from "../useStoreSearch";
import { useOnBoardingContext } from "../OnBoardingContext";
import { GfStoreDto } from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import { extractPlaceAndCoordinates } from "../../../../utils/utils";
import {
  generateOpeningHoursFromIndividualTimes,
  transformGoogleApiResponse,
} from "../utils/onboardingDataTransformer";
import {
  getLatLngFromGooglePlace,
  constructGoogleMapsUrl,
  formatStoreAddress,
  getStoreLatLngFromStore,
  MAP_CONFIG,
} from "../utils/googleMapsUtils";

interface InfoWindowData {
  position: { lat: number; lng: number };
  name: string;
  address: string;
}

interface GoogleMapSearchContextType {
  query: string;
  setQuery: (query: string) => void;
  mapCenter: { lat: number; lng: number };
  setMapCenter: (center: { lat: number; lng: number }) => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  infoWindow: InfoWindowData | null;
  setInfoWindow: (info: InfoWindowData | null) => void;
  googleResults: GooglePlaceResult[];
  setGoogleResults: (results: GooglePlaceResult[]) => void;
  googleLoading: boolean;
  setGoogleLoading: (loading: boolean) => void;
  googleError: string | null;
  setGoogleError: (error: string | null) => void;
  results: Array<{
    source: "api" | "google";
    data: GfStoreDto | GooglePlaceResult;
  }>;
  loading: boolean;
  error: string | null;
  searchStores: (query: string, transform?: any) => Promise<void>;
  handleSearch: () => Promise<void>;
  handleSelect: (item: {
    source: "api" | "google";
    data: GfStoreDto | GooglePlaceResult;
  }) => Promise<void>;
  isLoaded: boolean;
  hasSearched: boolean;
  setHasSearched: (searched: boolean) => void;
}

const GoogleMapSearchContext = createContext<GoogleMapSearchContextType | null>(
  null
);

export const useGoogleMapSearchContext = () => {
  const context = useContext(GoogleMapSearchContext);
  if (!context) {
    throw new Error("Components must be used within GoogleMapSearchProvider");
  }
  return context;
};

export const GoogleMapSearchProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { setSelectedStore, setOnboardingData } = useOnBoardingContext();
  const [query, setQuery] = useState("");
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    MAP_CONFIG.defaultCenter
  );
  const [zoom, setZoom] = useState<number>(MAP_CONFIG.defaultZoom);
  const [infoWindow, setInfoWindow] = useState<InfoWindowData | null>(null);
  const { results, loading, error, searchStores } = useStoreSearch();
  const isLoaded = useApiIsLoaded();
  const [googleResults, setGoogleResults] = useState<GooglePlaceResult[]>([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const googleSearch = (q: string): Promise<GooglePlaceResult[]> => {
    return new Promise((resolve) => {
      if (!window.google) return resolve([]);
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.textSearch({ query: q }, (results: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else {
          resolve([]);
        }
      });
    });
  };

  const getPlaceDetails = (
    placeId: string
  ): Promise<{ lat: number; lng: number } | undefined> => {
    return new Promise((resolve) => {
      if (!window.google) return resolve(undefined);
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.getDetails({ placeId }, (place: any, status: any) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry?.location
        ) {
          const loc = place.geometry.location;
          let lat: number | undefined, lng: number | undefined;
          if (typeof loc.lat === "function" && typeof loc.lng === "function") {
            lat = loc.lat();
            lng = loc.lng();
          } else if (
            typeof loc.lat === "number" &&
            typeof loc.lng === "number"
          ) {
            lat = loc.lat;
            lng = loc.lng;
          }
          if (
            typeof lat === "number" &&
            typeof lng === "number" &&
            isFinite(lat) &&
            isFinite(lng)
          ) {
            resolve({ lat, lng });
            return;
          }
        }
        resolve(undefined);
      });
    });
  };

  const getStoreDetails = async (googleMapUrl: string) => {
    try {
      console.log("🔍 Getting store details for URL:", googleMapUrl);
      const data = extractPlaceAndCoordinates(googleMapUrl);
      if (!data?.place) {
        console.warn("⚠️ No place name found in URL:", googleMapUrl);
        return null;
      }
      console.log("📍 Extracted place data:", data);
      const response =
        await adminApiService.gfStoreAdminControllerGetStoreDetailsUsingGoogleFindPlaceApi(
          data.place,
          data.latitude,
          data.longitude
        );
      if (!response?.storeInfo) {
        console.warn("⚠️ No store info in response:", response);
        return null;
      }
      console.log("✅ Store details retrieved successfully:");
      console.log("📋 Place Details:", response.placeDetails);
      console.log("🏪 Store Info:", response.storeInfo);
      const standardizedData = transformGoogleApiResponse(response);
      console.log("🔄 Standardized data:", standardizedData);
      setOnboardingData(standardizedData);
      return response;
    } catch (error) {
      console.error("❌ Error getting store details:", error);
      return null;
    }
  };

  const handleSearch = async () => {
    setHasSearched(true);
    setGoogleResults([]);
    setGoogleError(null);
    setGoogleLoading(false);
    await searchStores(query, async () => []);

    if (results.length === 0) {
      setGoogleLoading(true);
      try {
        const googlePlaces = await googleSearch(query);
        setGoogleResults(googlePlaces);
      } catch (e) {
        setGoogleError("Error searching Google Places");
      } finally {
        setGoogleLoading(false);
      }
    }
  };

  const handleApiStoreSelect = async (store: GfStoreDto) => {
    const name = store.address || store.signature || "Store";
    const address = formatStoreAddress(store);
    const latLng = getStoreLatLngFromStore(store);

    const {
      merchant,
      id: _id,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      ...restOfStore
    } = store;
    const {
      id: _merchantId,
      createdAt: _createdAtM,
      updatedAt: _updatedAtM,
      ...restOfMerchant
    } = merchant || {};

    const openingHours = generateOpeningHoursFromIndividualTimes(
      store.openTime || 9,
      store.closeTime || 17,
      store.saturdayOpenTime || 9,
      store.saturdayCloseTime || 17,
      store.sundayOpenTime || 9,
      store.sundayClosedTime || 17
    );

    const standardizedData = {
      merchant: restOfMerchant,
      store: { ...restOfStore, openingHours },
      metadata: { isFromGoogleApi: false, hasGooglePlaceDetails: false },
    };

    console.log("standardizedData", standardizedData);
    setOnboardingData(standardizedData);
    return { name, address, latLng };
  };

  const handleGooglePlaceSelect = async (place: GooglePlaceResult) => {
    const name = place.name;
    const address = place.formatted_address;
    const details = await getPlaceDetails(place.place_id);
    const latLng = details || getLatLngFromGooglePlace(place);

    if (latLng) {
      const googleMapUrl = constructGoogleMapsUrl(latLng.lat, latLng.lng, name);
      if (googleMapUrl) {
        await getStoreDetails(googleMapUrl);
      }
    }
    return { name, address, latLng };
  };

  const handleSelect = async (item: {
    source: "api" | "google";
    data: GfStoreDto | GooglePlaceResult;
  }) => {
    let name = "";
    let address = "";
    let latLng: { lat: number; lng: number } | undefined;

    if (item.source === "api") {
      const result = await handleApiStoreSelect(item.data as GfStoreDto);
      name = result.name;
      address = result.address;
      latLng = result.latLng;
    } else if (item.source === "google") {
      const result = await handleGooglePlaceSelect(
        item.data as GooglePlaceResult
      );
      name = result.name;
      address = result.address;
      latLng = result.latLng;
    }

    if (latLng) {
      setMapCenter(latLng);
      setZoom(MAP_CONFIG.selectedZoom as number);
      setInfoWindow({ position: latLng, name, address });
    }

    setSelectedStore({ source: item.source, store: item.data });
  };

  const contextValue: GoogleMapSearchContextType = {
    query,
    setQuery,
    mapCenter,
    setMapCenter,
    zoom,
    setZoom,
    infoWindow,
    setInfoWindow,
    googleResults,
    setGoogleResults,
    googleLoading,
    setGoogleLoading,
    googleError,
    setGoogleError,
    results,
    loading,
    error,
    searchStores,
    handleSearch,
    handleSelect,
    isLoaded,
    hasSearched,
    setHasSearched,
  };

  return (
    <GoogleMapSearchContext.Provider value={contextValue}>
      {children}
    </GoogleMapSearchContext.Provider>
  );
};
